// Utility functions for data processing and formatting

/**
 * Strips non-digit characters from a phone number and removes leading "1"
 * @param {string} phone - Raw phone number
 * @returns {string} - Cleaned phone number
 */
export const cleanPhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') return '';
  
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '');
  
  // Remove leading "1" if present and number is 11 digits
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    cleaned = cleaned.substring(1);
  }
  
  return cleaned;
};

/**
 * Extracts street number and address from a full address string
 * @param {string} fullAddress - Complete address string
 * @returns {object} - {streetNumber, address}
 */
export const parseAddress = (fullAddress) => {
  if (!fullAddress || typeof fullAddress !== 'string') {
    return { streetNumber: '', address: '' };
  }
  
  // Remove punctuation and normalize spaces
  const cleaned = fullAddress.trim().replace(/[.,;:]/g, '');
  
  // Try to extract leading number
  const match = cleaned.match(/^(\d+)\s+(.+)$/);
  
  if (match) {
    return {
      streetNumber: match[1],
      address: match[2].trim()
    };
  }
  
  // If no number found, return original as address
  return {
    streetNumber: '',
    address: cleaned
  };
};

/**
 * Formats a date from ISO string to MM/DD/YYYY
 * @param {string} isoDate - ISO date string
 * @returns {string} - Formatted date
 */
export const formatDateToMMDDYYYY = (isoDate) => {
  if (!isoDate) return '';
  
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date from MM/DD/YYYY to ISO string
 * @param {string} dateString - Date in MM/DD/YYYY format
 * @returns {string} - ISO date string
 */
export const formatDateToISO = (dateString) => {
  if (!dateString) return '';
  
  try {
    const [month, day, year] = dateString.split('/');
    const date = new Date(year, month - 1, day);
    
    if (isNaN(date.getTime())) return '';
    
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error parsing date:', error);
    return '';
  }
};

/**
 * Checks if a record has required fields
 * @param {object} record - Record to validate
 * @param {array} requiredFields - Array of required field names
 * @returns {boolean} - True if all required fields are present
 */
export const hasRequiredFields = (record, requiredFields) => {
  if (!record || !requiredFields) return false;
  
  return requiredFields.every(field => {
    const value = record[field];
    return value !== null && value !== undefined && value !== '';
  });
};

/**
 * Normalizes a name for comparison (removes extra spaces, converts to lowercase)
 * @param {string} name - Name to normalize
 * @returns {string} - Normalized name
 */
export const normalizeName = (name) => {
  if (!name || typeof name !== 'string') return '';
  
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Normalizes an email for comparison
 * @param {string} email - Email to normalize
 * @returns {string} - Normalized email
 */
export const normalizeEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  
  return email.trim().toLowerCase();
};

/**
 * Creates a HubSpot deal link from record ID
 * @param {string} recordId - HubSpot record ID
 * @returns {string} - HubSpot deal URL
 */
export const createHubSpotDealLink = (recordId) => {
  if (!recordId) return '';
  
  // HubSpot deal URL format
  return `https://app.hubspot.com/contacts/your-hub-id/deal/${recordId}`;
};

/**
 * Safely gets a nested property from an object
 * @param {object} obj - Object to search
 * @param {string} path - Dot-separated path (e.g., 'user.profile.name')
 * @param {*} defaultValue - Default value if property not found
 * @returns {*} - Property value or default
 */
export const getNestedProperty = (obj, path, defaultValue = '') => {
  if (!obj || !path) return defaultValue;
  
  try {
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    return value !== undefined ? value : defaultValue;
  } catch (error) {
    return defaultValue;
  }
};

/**
 * Debounces a function call
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Downloads data as CSV file
 * @param {array} data - Array of objects to export
 * @param {string} filename - Name of the file
 * @param {array} headers - Optional array of header objects {key, label}
 */
export const downloadCSV = (data, filename, headers = null) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.error('No data to export');
    return;
  }
  
  let csvContent = '';
  
  // Use provided headers or extract from first object
  const csvHeaders = headers || Object.keys(data[0]).map(key => ({ key, label: key }));
  
  // Add header row
  csvContent += csvHeaders.map(h => `"${h.label}"`).join(',') + '\n';
  
  // Add data rows
  data.forEach(row => {
    const values = csvHeaders.map(header => {
      const value = row[header.key] || '';
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    });
    csvContent += values.join(',') + '\n';
  });
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Validates HubSpot API token format
 * @param {string} token - Token to validate
 * @returns {boolean} - True if token appears valid
 */
export const validateHubSpotToken = (token) => {
  if (!token || typeof token !== 'string') return false;
  
  // Basic validation - HubSpot tokens are typically long alphanumeric strings
  return token.length > 20 && /^[a-zA-Z0-9-_]+$/.test(token);
};

/**
 * Generates a month/year string from a date
 * @param {string|Date} date - Date to process
 * @returns {string} - Month/Year format (e.g., "01/2024")
 */
export const getMonthYear = (date) => {
  if (!date) return '';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    
    return `${month}/${year}`;
  } catch (error) {
    console.error('Error getting month/year:', error);
    return '';
  }
};
